import {
  CurrencyInputV2,
  DocumentNumberFieldV2,
  FormFieldV2,
  SearchFieldV2 as SearchField,
  AritoIcon
} from '@/components/custom/erp';
import {
  hanThanhToanSearchColumns,
  khachHangSearchColumns,
  MA_CHUNG_TU,
  nhanVienSearchColumns,
  QUERY_KEYS
} from '@/constants';
import { HanThanhToan, KhachHang, NhanVien } from '@/types/schemas';
import { FormFieldActions, FormFieldState } from '../../hooks';
import { Label } from '@/components/ui/label';
import { FormMode } from '@/types/form';

interface BasicInfoTabProps {
  formMode: FormMode;
  formState: {
    state: FormFieldState;
    actions: FormFieldActions;
  };
}

export function BasicInfoTab({ formMode, formState: { state, actions } }: BasicInfoTabProps) {
  const rowClassName = 'flex items-center gap-2 xl:gap-3';
  const labelClassName = 'w-[120px] shrink-0 text-[13px] xl:w-[148px] 2xl:w-[160px]';

  return (
    <div className='overflow-x-auto'>
      <div className='min-w-[1180px] p-3 xl:min-w-0 xl:p-4'>
        <div className='grid grid-cols-12 gap-3 xl:gap-4 2xl:gap-5'>
          <div className='col-span-6 min-w-0 space-y-2'>
            <div className={rowClassName}>
              <Label className={labelClassName}>Loại đơn hàng</Label>
              <div className='w-[156px] xl:w-[168px] 2xl:w-[180px]'>
                <FormFieldV2
                  name='ma_ngv'
                  type='select'
                  disabled={formMode === 'view'}
                  options={[
                    { value: '5', label: 'Đơn hàng' },
                    { value: '4', label: 'Hợp đồng' }
                  ]}
                />
              </div>
            </div>

            <div className={rowClassName}>
              <Label className={labelClassName}>Mã khách hàng</Label>
              <div className='flex min-w-0 flex-1 items-center gap-2 xl:gap-3'>
                <div className='w-[156px] xl:w-[168px] 2xl:w-[200px]'>
                  <SearchField<KhachHang>
                    type='text'
                    name='ma_kh'
                    value={state.khachHang?.customer_code || ''}
                    searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}/`}
                    searchColumns={khachHangSearchColumns}
                    onRowSelection={actions.setKhachHang}
                    columnDisplay='customer_code'
                    dialogTitle='Danh mục khách hàng'
                    className='w-full'
                    disabled={formMode === 'view'}
                  />
                </div>

                <div className='flex min-w-0 flex-1 items-center gap-1.5 xl:gap-2'>
                  <Label className='w-[74px] shrink-0 text-[13px] xl:w-[82px]'>Mã số thuế</Label>
                  <div className='flex min-w-0 flex-1 items-center gap-2'>
                    <div className='min-w-0 max-w-[180px] flex-1 xl:max-w-[220px] 2xl:max-w-[240px]'>
                      <FormFieldV2
                        name='ma_so_thue'
                        type='text'
                        disabled={formMode === 'view'}
                        placeholder='Nhập và tra cứu'
                        value={state.khachHang?.tax_code || ''}
                      />
                    </div>
                    <button className='flex items-center justify-center border border-gray-200 px-2 py-1 xl:px-3 xl:py-1.5'>
                      <AritoIcon icon={15} className='shrink-0' />
                    </button>
                    <button className='flex items-center justify-center border border-gray-200 px-2 py-1 xl:px-3 xl:py-1.5'>
                      <AritoIcon icon={888} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className={rowClassName}>
              <Label className={labelClassName}>Tên khách hàng</Label>
              <div className='min-w-0 flex-1'>
                <FormFieldV2
                  name='ten_kh'
                  type='text'
                  disabled={formMode === 'view'}
                  value={state.khachHang?.customer_name || ''}
                  placeholder='Nhập tên khách hàng/đơn vị'
                />
              </div>
            </div>

            <div className={rowClassName}>
              <Label className={labelClassName}>Địa chỉ</Label>
              <div className='min-w-0 flex-1'>
                <FormFieldV2
                  name='dia_chi'
                  type='text'
                  disabled={formMode === 'view'}
                  value={state.khachHang?.address || ''}
                  placeholder='Nhập địa chỉ khách hàng'
                />
              </div>
            </div>

            <div className={rowClassName}>
              <Label className={labelClassName}>Mã nhân viên</Label>
              <SearchField<NhanVien>
                type='text'
                name='ma_nvbh'
                value={state.nhanVien?.ma_nhan_vien || state.khachHang?.sales_rep_data?.ma_nhan_vien || ''}
                relatedFieldValue={
                  state.nhanVien?.ho_ten_nhan_vien || state.khachHang?.sales_rep_data?.ho_ten_nhan_vien || ''
                }
                searchEndpoint={`/${QUERY_KEYS.NHAN_VIEN}/`}
                columnDisplay='ma_nhan_vien'
                displayRelatedField='ho_ten_nhan_vien'
                searchColumns={nhanVienSearchColumns}
                dialogTitle='Danh sách nhân viên bán hàng'
                onRowSelection={actions.setNhanVien}
                disabled={formMode === 'view'}
                className='w-100'
              />
            </div>

            <div className={rowClassName}>
              <Label className={labelClassName}>Diễn giải</Label>
              <div className='min-w-0 flex-1'>
                <FormFieldV2 name='dien_giai' type='text' disabled={formMode === 'view'} />
              </div>
            </div>

            <div className={rowClassName}>
              <Label className={labelClassName}>Ngày hiệu lực</Label>
              <div className='w-[180px]'>
                <FormFieldV2 name='ngay_hl' type='date' disabled={formMode === 'view'} />
              </div>
            </div>
          </div>

          <div className='col-span-3 min-w-0 space-y-2 pt-[3rem] xl:pt-[3.4rem]'>
            <div className='h-6 xl:h-8'></div>

            <div className={rowClassName}>
              <FormFieldV2
                name='ong_ba'
                label='Người nhận'
                labelClassName='w-[90px] shrink-0 text-[13px] xl:w-[104px] 2xl:w-[112px]'
                fieldClassName='w-full text-red-600'
                disabled={formMode === 'view'}
                className='w-[250px] xl:w-[280px] 2xl:w-[280px]'
                value={state.khachHang?.contact_person || ''}
                placeholder='Nhập tên người liên hệ'
              />
            </div>

            <div className={rowClassName}>
              <FormFieldV2
                name='e_mail'
                label='Email'
                labelClassName='w-[90px] shrink-0 text-[13px] xl:w-[104px] 2xl:w-[112px]'
                fieldClassName='w-full'
                className='w-[250px] xl:w-[280px] 2xl:w-[280px]'
                disabled={formMode === 'view'}
                value={state.khachHang?.email || ''}
                placeholder='Nhập email'
              />
            </div>

            <div className={rowClassName}>
              <div className='flex min-w-0 flex-1 items-center gap-3'>
                <Label className='w-[90px] shrink-0 text-[13px] xl:w-[104px] 2xl:w-[105px]'>Hạn thanh toán</Label>
                <SearchField<HanThanhToan>
                  type='text'
                  name='ma_tt'
                  value={state.hanThanhToan?.ten_tt || state.khachHang?.payment_term_data?.ten_tt || ''}
                  searchEndpoint={`/${QUERY_KEYS.HAN_THANH_TOAN}/`}
                  columnDisplay='ma_tt'
                  searchColumns={hanThanhToanSearchColumns}
                  dialogTitle='Danh mục hạn thanh toán'
                  className='w-full'
                  onRowSelection={actions.setHanThanhToan}
                  placeholder='Hạn thanh toán'
                  disabled={formMode === 'view'}
                />
              </div>
            </div>
          </div>

          <div className='col-span-3 min-w-0'>
            <div className='space-y-2 pr-2'>
              <DocumentNumberFieldV2
                ma_ct={MA_CHUNG_TU.BAN_HANG.DON_HANG}
                quyenChungTu={state.quyenChungTu}
                onQuyenChungTuChange={actions.setQuyenChungTu}
                soChungTu={state.soChungTu}
                onSoChungTuChange={actions.setSoChungTu}
                disabled={formMode === 'view'}
                className='flex items-center gap-3'
                labelClassName='w-[92px] shrink-0 text-[13px] xl:w-[104px] 2xl:w-28'
                classNameSearchField='min-w-0 flex-1'
                formMode={formMode}
              />

              <FormFieldV2
                label='Ngày chứng từ'
                name='ngay_ct'
                type='date'
                disabled={formMode === 'view'}
                className='w-full'
                labelClassName='w-[92px] shrink-0 text-[13px] xl:w-[104px] 2xl:w-28'
              />

              <FormFieldV2
                label='Số hợp đồng'
                name='so_ct2'
                type='text'
                disabled={formMode === 'view'}
                className='w-full'
                labelClassName='w-[92px] shrink-0 text-[13px] xl:w-[104px] 2xl:w-28'
              />

              <CurrencyInputV2
                formMode={formMode}
                className='flex items-center gap-3'
                labelClassName='w-[92px] shrink-0 text-[13px] xl:w-[104px] 2xl:w-28'
                classNameInput='min-w-0 flex-1'
              />

              <FormFieldV2
                name='status'
                type='select'
                disabled={formMode === 'view'}
                label='Trạng thái'
                className='w-full'
                labelClassName='w-[92px] shrink-0 text-[13px] xl:w-[104px] 2xl:w-28'
                fieldClassName='w-full'
                options={[
                  { value: '0', label: 'Lập chứng từ' },
                  { value: '1', label: 'Chờ duyệt' },
                  { value: '5', label: 'Đã duyệt' }
                ]}
              />

              <div className='mt-1 flex'>
                <div className='mb-4 h-2 w-[92px] shrink-0 xl:w-[104px] 2xl:w-28' />
                <FormFieldV2
                  label='Dữ liệu nhận được'
                  name='transfer_yn'
                  type='checkbox'
                  disabled={true}
                  className='w-full'
                  labelClassName='w-28 text-[13px] xl:w-32'
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
